/**
 * @architecture-header 1
 * @layer app
 * @domain workspace
 * @responsibility Project TanStack Query entities into the dataset the workspace screens render in connected mode.
 * @state-owner TanStack Query owns the entities; the dataset store holds the rendered projection
 * @allowed-imports React; app environment; module public queries; dataset store; shared view types
 * @forbidden direct API client; UI markup; demo-mode branching inside modules
 * @public-api WorkspaceShell
 * @side-effects hydrates dataset entities and collection load status when queries change
 * @tests WorkspaceDataHydrator.test.tsx and workspace Playwright flows
 */

import type { Role } from '@vispo/contracts';
import { useEffect, useLayoutEffect } from 'react';
import { useActivities } from '../../modules/activities';
import { useAssistants } from '../../modules/assistants';
import { useClassrooms } from '../../modules/classrooms';
import { hasHttpStatus } from '../../shared/lib/api-error';
import type { ActivityRecord, ClassRecord } from '../../shared/lib/model';
import { useDatasetStore } from '../../shared/model/dataset-store';
import { useWorkspaceEnvironment } from '../config/environment';

/** The class shape the demo dataset publishes. */
function toClassRecord(value: {
  id: string;
  name: string;
  subject: string;
  studentCount: number;
  joinCode: string;
  students: string[];
}): ClassRecord {
  return {
    id: value.id,
    name: value.name,
    subtitle: value.subject,
    code: value.joinCode,
    students: value.students,
  };
}

/** The activity shape the demo dataset publishes. */
function toActivityRecord(
  value: {
    id: string;
    title: string;
    status: 'parsing' | 'review' | 'ready';
    questions: number;
    needsReview: number;
    updatedLabel: string;
    classIds: string[];
  },
  classIds: readonly string[],
): ActivityRecord {
  const usedByClassIndexes = value.classIds
    .map((classId) => classIds.indexOf(classId))
    .filter((index) => index >= 0);
  return {
    id: value.id,
    name: value.title,
    questionCount: value.questions,
    status: value.status,
    tags: [],
    createdAt: 0,
    updatedAt: 0,
    needsReview: value.needsReview,
    classIndex: usedByClassIndexes[0] ?? null,
    updatedLabel: value.updatedLabel,
    reviewed: Array.from({ length: 4 }, (_, index) => (index < value.needsReview ? 0 : 1)),
    usedByClassIndexes,
  };
}

/**
 * Demo mode keeps the deterministic  dataset. Connected mode replaces it with the
 * entities the API returns, so both paths render the identical presentation layer.
 */
export function WorkspaceDataHydrator({
  activeContextId,
  organizationId,
  workspaceRole,
}: {
  activeContextId: string;
  organizationId?: string | null;
  workspaceRole: Role;
}) {
  const environment = useWorkspaceEnvironment();
  const classroomsQuery = useClassrooms(activeContextId, { audience: workspaceRole });
  const activitiesQuery = useActivities(activeContextId, workspaceRole, organizationId);
  const assistantsQuery = useAssistants(
    activeContextId,
    organizationId,
    workspaceRole === 'teacher',
  );
  const hydrate = useDatasetStore((state) => state.hydrate);
  const clearConnected = useDatasetStore((state) => state.clearConnected);
  const setClassroomDirectoryStatus = useDatasetStore((state) => state.setClassroomDirectoryStatus);
  const setAssistantDirectoryStatus = useDatasetStore((state) => state.setAssistantDirectoryStatus);

  useLayoutEffect(() => {
    if (environment.demoMode || !activeContextId) return;
    clearConnected();
  }, [activeContextId, clearConnected, environment.demoMode]);

  useLayoutEffect(() => {
    if (environment.demoMode) return;
    setClassroomDirectoryStatus(
      classroomsQuery.isError
        ? hasHttpStatus(classroomsQuery.error, 403)
          ? 'denied'
          : 'error'
        : classroomsQuery.isPending || classroomsQuery.isFetching
          ? 'loading'
          : 'ready',
    );
  }, [
    classroomsQuery.error,
    classroomsQuery.isError,
    classroomsQuery.isFetching,
    classroomsQuery.isPending,
    environment.demoMode,
    setClassroomDirectoryStatus,
  ]);

  useLayoutEffect(() => {
    if (environment.demoMode) return;
    if (workspaceRole !== 'teacher') {
      setAssistantDirectoryStatus('denied');
      return;
    }
    setAssistantDirectoryStatus(
      assistantsQuery.isError
        ? hasHttpStatus(assistantsQuery.error, 403)
          ? 'denied'
          : 'error'
        : assistantsQuery.isPending || assistantsQuery.isFetching
          ? 'loading'
          : 'ready',
    );
  }, [
    assistantsQuery.error,
    assistantsQuery.isError,
    assistantsQuery.isFetching,
    assistantsQuery.isPending,
    environment.demoMode,
    setAssistantDirectoryStatus,
    workspaceRole,
  ]);

  useEffect(() => {
    if (environment.demoMode) return;
    const classrooms = classroomsQuery.data;
    if (!classrooms) return;
    hydrate({ classes: classrooms.map(toClassRecord) });
  }, [classroomsQuery.data, environment.demoMode, hydrate]);

  useEffect(() => {
    if (environment.demoMode) return;
    const activities = activitiesQuery.data;
    const classrooms = classroomsQuery.data;
    if (!activities || !classrooms) return;
    const classIds = classrooms.map((item) => item.id);
    hydrate({ activities: activities.map((item) => toActivityRecord(item, classIds)) });
  }, [activitiesQuery.data, classroomsQuery.data, environment.demoMode, hydrate]);

  useEffect(() => {
    if (environment.demoMode) return;
    if (workspaceRole !== 'teacher' || !assistantsQuery.data) return;
    hydrate({ assistants: assistantsQuery.data });
  }, [assistantsQuery.data, environment.demoMode, hydrate, workspaceRole]);

  return null;
}
